import { EyeOutlined } from '@ant-design/icons';
import { ClassKey } from '@const/class.constants';
import { Item, ItemGroupPreview } from '@dto/architecture.dto';
import { ItemGroup } from '@dto/customGroup.dto';
import { Button, ButtonProps, Checkbox, Form, Radio, RadioChangeEvent, RadioGroupProps } from "antd";
import { CheckboxChangeEvent, CheckboxOptionType } from "antd/es/checkbox";
import { AxiosError } from 'axios';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isClassLPAR, isClassWithHwType } from 'src/data/Class';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import { XmSpinner } from '../spinner/XmSpinner';
import Items from './children/Items';
import RegexList, { isPatternValid } from './children/RegexList';
import TagListItems from './children/TagList';
import AllItems, { AllItemsContext, HwProps } from './comp/AllItems';
import AllTags from './comp/AllTags';
import { CustomFormCompProps } from './comp/Items';
import { makePreviewApiData } from './comp/preview/CgPreviewHandler';
import { regexApi, usePreviewModal } from "./comp/preview/Preview";
import ExcludeItems from './exclude/Items';
import useParentTypes from './hook/useParentTypes';
import ParentItems from './parent/Items';
import RegexListParent from './parent/RegexList';
import TagListParent from './parent/TagList';
import './Rule.less';
import { EnableItemId, RuleGroup, Props as RuleProps } from './XmRuleList';

export const RuleContext = createContext<RulePropsBase & FormOperations>(undefined);
type ChangeCallback = (rule: RuleGroup) => RuleGroup;
interface FormOperations {
  makeChange: (callback: ChangeCallback, filterByParents?: boolean) => void;
  getPath: (path: string | (string | number)[]) => (string | number)[];
  getValue: <T>(path?: string | (string | number)[]) => T;
  filterByParents: () => void;
}

export type FilterModeType = 'items' | 'regex' | 'tags';

type RulePropsBase = Pick<ItemGroupPreview, 'linuxExclude'> & ({
  mountPoint?: (string | number)[];
  withoutParents?: boolean;
  onModeChange?: (mode: FilterModeType) => void;
});

export type ApiPrams = Record<string, unknown>;
export type AdditionalApiParams = Partial<{
  main: ApiPrams;
  parents: Partial<Record<'type' | 'select', ApiPrams>>;
  exclude: Partial<Record<'type' | 'select', ApiPrams>>;
  items: Partial<Record<'select', ApiPrams>>;
  preview: Partial<{
    parents: Partial<Record<'all' | 'item', ApiPrams>>;
    exclude: Partial<Record<'all' | 'item', ApiPrams>>;
    items: Partial<Record<'all' | 'item', ApiPrams>>;
  }>;
}>;

// additionalPayload => adding/overwriting every preview/regex payload api call.
//* Enabled only hw_type. If another prop is needed proceed with caution.
export type AdditionalPayload = Partial<Pick<ItemGroupPreview, 'hw_type' | 'withDeleted'>>;
type Props = Pick<ItemGroupPreview, 'linuxExclude'> & Omit<RuleProps, 'additionalApiParams' | 'mountPoint'> & {
  additionalApiParams?: AdditionalApiParams;
  additionalPayload?: AdditionalPayload;
  onModeChange?: (mode: FilterModeType) => void;
};

export type PropsRule<T> = T & ({ mountPoint?: string[]; dependenciesPoint?: (string | number)[]; } | { mountPoint: [number, ...string[]]; dependenciesPoint: (string | number)[]; });

export const AdditionalApiPropsContext = createContext<AdditionalApiParams & { additionalPayload: AdditionalPayload; }>(undefined);
export default function XmRule({
  mountPoint,
  additionalApiParams,
  additionalPayload,
  linuxExclude = false,
  withoutParents = false,
  ...rest
}: PropsRule<Props>) {

  const dependenciesPoint = mountPoint ? typeof mountPoint[0] === 'number' ? rest.dependenciesPoint : mountPoint : undefined;
  const form = Form.useFormInstance<unknown>();

  const makeChange = useMemo(() => (callback?: ChangeCallback, filterByParents = false) => {
    const redirectFilterItems = ({ parents, items }: RuleGroup) => {
      const { cls, subsystem, hw_type } = rest;
      return filterItems(items.map(({ item_id }) => item_id), {
        regex: ['.*'],
        class: cls,
        hw_type: ruleHwType(cls, hw_type),
        subsystem,
        parents: parents && {
          intersection: parents.intersection,
          item_ids: parents.items?.map(item => item.item_id),
          regex: parents.regex?.filter(r => r.regex && r.subsystem),
          tags: parents.tags
        },
        ...additionalPayload
      });
    };

    if (!mountPoint) {
      const { items, regex, tags, parents, exclude, ...other } = form.getFieldsValue() as RuleGroup; // other include rest of the form data
      const r = {
        items,
        regex,
        tags,
        parents,
        exclude
      };

      const newState = callback?.(r) || r;
      if (!filterByParents || filterByParents && !newState.items?.length) {
        form.setFieldsValue({
          ...other,
          ...newState
        });

        return;
      }

      redirectFilterItems(newState).then(
        data => {
          const filteredItems: EnableItemId[] = newState.items.map(item => ({ ...item, disabled: !data.includes(item.item_id) }));
          form.setFieldsValue({
            ...other,
            ...newState,
            items: filteredItems
          });

          void form.validateFields(['items']);
        },
        (reason: AxiosError) => Log.error('Failed to fetch items!', reason)
      );
    }

    const rule: RuleGroup = form.getFieldValue(dependenciesPoint);
    const newState = callback?.(rule) || rule;
    if (!filterByParents || filterByParents && !newState.items?.length) {
      form.setFieldValue(dependenciesPoint, newState);
      return;
    }

    redirectFilterItems(newState).then(
      data => {
        const filteredItems: EnableItemId[] = newState.items.map(item => ({ ...item, disabled: !data.includes(item.item_id) }));
        form.setFieldValue(dependenciesPoint, {
          ...newState,
          items: filteredItems
        });

        void form.validateFields([[...dependenciesPoint, 'items']]);
      },
      (reason: AxiosError) => Log.error('Failed to fetch items!', reason)
    );
  }, [mountPoint, rest.dependenciesPoint]);

  const filterByParents = useCallback(() => makeChange(undefined, true), [makeChange]);

  const getPath: FormOperations['getPath'] = useCallback(path => {
    if (!mountPoint)
      return typeof path === 'string' ? [path] : path;

    if (typeof path === 'string')
      return [...mountPoint, path];

    return [...mountPoint, ...path];
  }, [mountPoint]);

  const getValue: FormOperations['getValue'] = useCallback(path => {
    if (!mountPoint) {
      if (!path)
        return form.getFieldsValue(true);

      if (typeof path === 'string')
        return form.getFieldValue([path]);

      return form.getFieldValue(path);
    }

    if (!path)
      return form.getFieldValue(dependenciesPoint);

    if (typeof path === 'string')
      return form.getFieldValue([...dependenciesPoint, path]);

    return form.getFieldValue([...dependenciesPoint, ...path]);
  }, [mountPoint]);

  //TODO: Use shouldUpdate as callback prop. Limit to necessary dependencies.
  return (
    <AdditionalApiPropsContext.Provider value={{ ...additionalApiParams, additionalPayload }}>
      <TagsWrapper noTagMode={rest.noTagMode}>
        <AllItems {...rest} additionalApiParams={additionalApiParams?.items?.select} additionalPayload={additionalPayload} linuxExclude={linuxExclude}>
          <RuleContext.Provider value={{ ...rest, mountPoint: dependenciesPoint, getPath, getValue, makeChange, filterByParents, linuxExclude }}>
            <div className="xm-rule">
              {!withoutParents &&
                <Form.Item shouldUpdate>
                  {() =>
                    <Form.Item noStyle name={getPath('parents')}>
                      <FilterModeParent />
                    </Form.Item>
                  }
                </Form.Item>
              }
              <Form.Item shouldUpdate>
                {() => <FilterMode />}
              </Form.Item>
              <Form.Item shouldUpdate>
                {() =>
                  <Form.Item noStyle name={getPath('exclude')}>
                    <ExcludeSelect />
                  </Form.Item>
                }
              </Form.Item>
            </div>
          </RuleContext.Provider>
        </AllItems>
      </TagsWrapper>
    </AdditionalApiPropsContext.Provider>
  );
}

interface RuleFormItem extends CustomFormCompProps<RuleGroup> {
  getPath: FormOperations['getPath'];
}

function TagsWrapper({ children, noTagMode }: PropsWithChildren<{ noTagMode: boolean; }>) {
  if (noTagMode)
    return children;

  return <AllTags>
    {children}
  </AllTags>;
}

function FilterModeParent({ value: parents, onChange }: CustomFormCompProps<RuleGroup['parents']>) {
  const { cls, subsystem, hw_type, } = useContext(AllItemsContext);
  const { preview, additionalPayload } = useContext(AdditionalApiPropsContext);
  const { makeChange: makeChangeFromContext, getPath, filterByParents, linuxExclude, getValue } = useContext(RuleContext);
  const [showPreview, contextHolderRegex] = usePreviewModal();
  const invalidBaseProps = invalidHwProps({ cls, hw_type, subsystem });
  const [parentTypes, isLoadingParentTypes] = useParentTypes(cls, subsystem, hw_type);
  const rule = getValue<RuleGroup>();

  // When parents changed, it is always wanted to filter children. So simplified context call by setting second parm to true.
  const makeChange = (callback: ChangeCallback) => makeChangeFromContext(callback, true);

  const handleModeChange = ({ target: { value } }: RadioChangeEvent) => {
    makeChange(rule => {
      function resetItems(): Pick<RuleGroup, 'items' | 'regex' | 'tags'> {
        if (rule.items)
          return { ...rule, items: [], regex: undefined, tags: undefined };

        return { ...rule, items: undefined };
      }

      const newRule: RuleGroup = {
        ...resetItems(),
        parents: {
          ...rule.parents,
          items: undefined,
          regex: undefined,
          tags: undefined,
          [value as ModeType]: [],
        },
      };

      const subsystem = undefined;
      if (value as ModeType === 'regex')
        newRule.parents.regex = [{ subsystem, regex: '.*' }];

      if (value as ModeType === 'tags')
        newRule.parents.tags = [{ tag: undefined, tag_id: undefined, subsystem }];

      return newRule;
    });
  };

  const handlePreview = () => {
    const additionalApiParams = { ...preview?.parents?.all, intersection: rule.parents.intersection };
    const baseData = {
      cls,
      subsystem,
      hw_type,
      parent: true,
      strictAcl: false,
      additionalApiParams,  // additionalApiParams.preview.parents.all
    };

    if (parents.tags) {
      showPreview({
        ...baseData,
        data: parents?.tags.map((r): ItemGroupPreview => ({
          class: cls,
          subsystem: r.subsystem,
          hw_type: ruleHwType(cls, hw_type),
          tags: [r.tag_id],
          linuxExclude,
          ...additionalPayload
        })).filter(data => !!data.subsystem),
      });

      return;
    }

    showPreview({
      ...baseData,
      data: parents?.regex.map((r): ItemGroupPreview => ({
        subsystem: r.subsystem,
        hw_type: ruleHwType(cls, hw_type),
        class: cls,
        regex: [r.regex],
        linuxExclude,
        ...additionalPayload
      })).filter(data => !!data.subsystem),
    });
  };

  const handleEnableParents = ({ target: { checked } }: CheckboxChangeEvent) => {
    onChange?.(checked ? { ...defaultRuleParent } : undefined);
    if (!checked)
      filterByParents();
  };

  const { readonly } = GLOB.userInfo;
  const isDisabled = readonly || !parents || !parents.regex && !parents.items && !parents.tags || invalidBaseProps;

  const isPreviewDisabled = !parents || !!parents?.items || parents.regex?.every(({ subsystem }) => !subsystem) || parents.tags?.every(({ tag_id }) => !tag_id) || invalidBaseProps || parents.regex?.some(pattern => !isPatternValid(pattern.regex));

  return (
    <div className='xm-rule-filter' data-testid='item-select-parents-section'>
      <div className={!!parents ? 'xm-rule-controllers' : 'xm-rule-controllers disabled'}>
        <Checkbox
          key={'is-parent'}
          className='xm-regex-checkbox header-bold'
          onChange={handleEnableParents}
          disabled={readonly || invalidBaseProps || isLoadingParentTypes || !parentTypes.length}
          checked={!!parents}
        >
          Filter by Parents
        </Checkbox>
        {isLoadingParentTypes && <XmSpinner size='small' style={{ margin: 'auto 0', position: 'inherit' }} />}
        <div className='xm-radio-select'>
          <Form.Item name={getPath(['parents', 'intersection'])} noStyle>
            <ConjunctionRadioSelect disabled={isDisabled} onChange={filterByParents} />
          </Form.Item>
          <ModeRadioSelect disabled={isDisabled} regex={parents?.regex} items={parents?.items} onChange={handleModeChange} parent />
          <span style={{ visibility: parents?.items ? 'hidden' : undefined }}>
            <ControlBtn
              icon={<EyeOutlined />}
              onClick={handlePreview}
              disabled={isPreviewDisabled}
            />
          </span>
        </div>
      </div>
      {parents?.items &&
        <div className='xm-form-no-message'>
          <Form.Item
            className='xm-select-wrapper'
            name={getPath(['parents', 'items'])}
            rules={[
              {
                validator: (_, values: Item[]) => {
                  if (values.length > 0)
                    return Promise.resolve();

                  return Promise.reject(new Error('Select at least one item.'));
                }
              }
            ]}
          >
            <ParentItems parentTypes={parentTypes} />
          </Form.Item>
        </div>
      }
      {parents?.regex && <RegexListParent parentTypes={parentTypes} />}
      {parents?.tags && <TagListParent parentTypes={parentTypes} />}
      {contextHolderRegex}
    </div>
  );
}

function FilterMode() {
  const { cls, subsystem, hw_type } = useContext(AllItemsContext);
  const { makeChange, getPath, getValue, linuxExclude, mountPoint, onModeChange } = useContext(RuleContext);
  const rule = getValue<RuleGroup>();
  const { items: itemsParams, additionalPayload } = useContext(AdditionalApiPropsContext);
  const [showPreview, contextHolderRegex] = usePreviewModal();
  const invalidBaseProps = invalidHwProps({ cls, hw_type, subsystem });
  const [baseInfoPrev, setBaseInfoPrev] = useState({ cls, subsystem, hw_type });

  const [mode, setMode] = useState<FilterModeType>(() => {
    return rule ? rule.items ? 'items' : rule.regex ? 'regex' : 'tags' : 'items';
  });

  useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  if (cls != baseInfoPrev.cls || subsystem != baseInfoPrev.subsystem || hw_type != baseInfoPrev.hw_type) {
    setBaseInfoPrev({ cls, subsystem, hw_type });
    setMode('items');
  }

  const handlePreview = () => {
    const data = {
      ...makePreviewApiData({ cls, hwType: hw_type, subsystem, parents: rule.parents, regex: rule.regex, tags: rule.tags }, linuxExclude),
      ...additionalPayload
    };

    showPreview({
      subsystem,
      hw_type,
      data,
      additionalApiParams: itemsParams?.select || {},
    });
  };

  const handleModeChange = ({ target: { value } }: RadioChangeEvent) => makeChange(rule => {
    setMode(value);
    const newRule = {
      ...rule,
      items: undefined,
      regex: undefined,
      tags: undefined,
      // [value as ModeType]: [],
    };

    if (value as ModeType == 'items')
      newRule.exclude = undefined;

    return newRule;
  });

  const { readonly } = GLOB.userInfo;

  return <>
    <div className='xm-rule-filter' data-testid='item-select-items-section'>
      <div className='xm-rule-controllers'>
        <span className='header-bold'>Select Items</span>
        <div className='xm-radio-select'>
          <ModeRadioSelect onChange={handleModeChange} regex={mode === 'regex' ? [] : undefined} items={mode === 'items' ? [] : undefined} disabled={readonly || invalidBaseProps} />
          <span style={{ visibility: rule?.items ? 'hidden' : undefined }}>
            <ControlBtn
              icon={<EyeOutlined />}
              onClick={handlePreview}
              className='rule'
              disabled={rule?.regex?.every(r => !r.length) || rule?.tags?.every(t => !t.tag_id) || invalidBaseProps || rule?.regex?.some(pattern => !isPatternValid(pattern))}
            />
          </span>
        </div>
      </div>
      <div className='xm-items-container'>
        {mode === 'items' &&
          <div className='xm-form-no-message' >
            <Form.Item
              className='xm-select-wrapper'
              name={getPath('items')}
              dependencies={[[...mountPoint, 'parents']]}
              rules={[
                {
                  validator: (_, values: EnableItemId[]) => {
                    if (!values || values?.some(item => !item.disabled))
                      return Promise.resolve();

                    return Promise.reject(new Error('All items are disabled. At least one item should be enabled.'));
                  }
                }
              ]}
            >
              <Items />
            </Form.Item>
          </div>
        }
        {mode === 'regex' && <RegexList />}
        {mode === 'tags' && <TagListItems />}
      </div>
      {contextHolderRegex}
    </div>
  </>;
}

function ExcludeSelect({ value: exclude, onChange }: CustomFormCompProps<RuleGroup['exclude']>) {
  const { cls, subsystem, hw_type } = useContext(AllItemsContext);
  const { getValue, linuxExclude, getPath } = useContext(RuleContext);
  const { preview, additionalPayload } = useContext(AdditionalApiPropsContext);
  const [showPreview, contextHolderRegex] = usePreviewModal();
  const invalidBaseProps = invalidHwProps({ cls, hw_type, subsystem });
  const parents = getValue<RuleGroup['parents']>('parents');
  const items = getValue<RuleGroup['items']>('items');

  const handlePreview = () => {
    const regex = getValue<RuleGroup['regex']>('regex');
    const tags = getValue<RuleGroup['tags']>('tags');
    const data: ItemGroupPreview[] = [
      { ...makePreviewApiData({ cls, hwType: hw_type, subsystem, parents, regex: exclude?.regex, tags: exclude?.tags }, linuxExclude), ...additionalPayload },
      { ...makePreviewApiData({ cls, hwType: hw_type, subsystem, parents, regex, tags }, linuxExclude), ...additionalPayload },
    ];

    showPreview({
      subsystem,
      hw_type,
      data,
      additionalApiParams: preview?.exclude?.all ? { ...preview.exclude.all, intersection: true } : { intersection: true },
    });
  };

  const handleModeChange = ({ target: { value } }: RadioChangeEvent) => {
    onChange?.({
      items: undefined,
      [value as ModeType]: [],
      regex: value as ModeType == 'regex' ? ['.*'] : undefined,
      tags: value as ModeType == 'tags' ? [{ tag: undefined, tag_id: undefined }] : undefined,
    });
  };

  const handleEnableExclude = ({ target: { checked } }: CheckboxChangeEvent) => onChange?.(checked ? { ...defaultRuleExclude } : undefined);
  const isPreviewDisabled = !exclude || !!exclude?.items || exclude.tags?.every(({ tag_id }) => !tag_id) || invalidBaseProps || exclude.regex?.some(pattern => !isPatternValid(pattern));
  const { readonly } = GLOB.userInfo;
  return (
    <div className='xm-rule-filter' data-testid='item-select-exclude-section'>
      <div className={!!exclude ? 'xm-rule-controllers' : 'xm-rule-controllers disabled'}>
        <Checkbox
          key={'is-parent'}
          onChange={handleEnableExclude}
          className='xm-regex-checkbox header-bold'
          disabled={readonly || invalidBaseProps || !!items}
          checked={!!exclude}
        >
          Exclude Items
        </Checkbox>
        <div className='xm-radio-select'>
          <ModeRadioSelect onChange={handleModeChange} regex={exclude?.regex} items={exclude?.items} disabled={invalidBaseProps || !exclude} />
          <span style={{ visibility: exclude?.items ? 'hidden' : undefined }}>
            <ControlBtn
              icon={<EyeOutlined />}
              onClick={handlePreview}
              className='rule'
              disabled={isPreviewDisabled}
            />
          </span>
        </div>
      </div>
      <div className='xm-items-container'>
        {exclude?.items &&
          <div className='xm-form-no-message'>
            <Form.Item
              className='xm-select-wrapper'
              name={getPath(['exclude', 'items'])}
              rules={[
                {
                  validator: (_, values: Item[]) => {
                    if (values.length > 0)
                      return Promise.resolve();

                    return Promise.reject(new Error('Select at least one item.'));
                  }
                }
              ]}
            >
              <ExcludeItems />
            </Form.Item>
          </div>
        }
        {exclude?.regex && <RegexList formItemName={['exclude', 'regex']} />}
        {exclude?.tags && <TagListItems formItemName={['exclude', 'tags']} />}
      </div>
      {contextHolderRegex}
    </div>
  );
}

interface ModeRadioSelectProps extends RadioGroupProps {
  items?: unknown[];
  regex?: unknown[];
  parent?: boolean; // TODO: remove after cross-platform bug solved
}

export type ModeType = keyof Omit<RuleGroup, 'parents'>;

function ModeRadioSelect({ items, regex, parent, ...rest }: ModeRadioSelectProps) {
  const { noTagMode, hw_type } = useContext(AllItemsContext);
  // TODO: remove disabled after cross-platform bug solved
  const options: CheckboxOptionType[] = noTagMode ? [
    { label: 'Select', value: 'items' },
    { label: 'RegEx', value: 'regex', disabled: hw_type === ClassKey.VIRTUALIZATION && parent },
  ] : [
    { label: 'Select', value: 'items' },
    { label: 'RegEx', value: 'regex' },
    { label: 'Tags', value: 'tags' }
  ];

  const { readonly } = GLOB.userInfo;

  if (readonly) {
    for (const o of options) {
      o.disabled = true;
    }
  }

  const value: ModeType = (!rest.disabled) ?
    items ? 'items' :
      regex ? 'regex' : 'tags'
    : undefined;

  return (
    <Radio.Group
      options={options}
      optionType="button"
      size="small"
      {...rest}
      value={value}
    />
  );
}

function ConjunctionRadioSelect(props: RadioGroupProps) {
  const options: CheckboxOptionType[] = [
    { label: 'AND', value: true },
    { label: 'OR', value: false },
  ];

  return (
    <Radio.Group
      options={options}
      optionType="button"
      size="small"
      {...props}
    />
  );
}

export function ControlBtn(props: ButtonProps) {
  const className = props.className ? `xm-btn-borderless xm-btn-controller ${props.className}` : 'xm-btn-borderless xm-btn-controller';

  return <Button
    size="small"
    {...props}
    className={className}
  />;
}

export const defaultRuleParent: RuleGroup['parents'] = {
  intersection: false,
  regex: undefined,
  items: [],
  tags: undefined
};

export const defaultRuleExclude: RuleGroup['exclude'] = {
  regex: undefined,
  items: [],
  tags: undefined
};

export const defaultRule: RuleGroup = {
  items: [],
  regex: undefined,
  tags: undefined,
  parents: undefined,
  exclude: undefined
};

export function filterItems(iids: string[], ...data: Parameters<typeof regexApi>): Promise<string[]> {
  return regexApi(...data).then(
    ({ data: { data } }) => iids.filter(id => data.some(item => item.item_id === id)),
    (reason: string) => { throw new Error(reason); }
  );
}

export function invalidHwProps({ cls, hw_type, subsystem }: HwProps) {
  return isClassLPAR(cls) ? !hw_type || !subsystem : !subsystem;
}

export function ruleHwType(cls: string, hwType?: string) {
  return isClassWithHwType(cls) ? hwType || undefined : undefined;
}

export const makeItemOfList = (cls: string) => ({ label, item_id, subsystem }: Item) => {
  return isClassLPAR(cls) ? { key: item_id, title: `${subsystem}/${label}` } : { key: item_id, title: label };
};

export function formatRuleDataForApi<T extends RuleGroup>(rule: T): Omit<T, "items" | "parents" | "exclude"> & ItemGroup {
  const { items, parents, exclude, ...restRule } = rule;
  return {
    ...restRule,
    item_ids: items?.filter((item) => !item.disabled).map((item) => item.item_id),
    tags: restRule.tags?.map((item) => item.tag_id),
    parents: parents && {
      ...parents,
      item_ids: parents.items?.map((item) => item.item_id),
      tags: parents.tags?.map((item) => ({ tag_id: item.tag_id, subsystem: item.subsystem })),
    },
    exclude: exclude && {
      ...exclude,
      item_ids: exclude.items?.map((item) => item.item_id),
      tags: exclude.tags?.map((item) => item.tag_id),
    },
  };
}

export function validRule(rule: RuleGroup) {
  return !!rule.items?.length || (rule.regex?.length && rule.regex?.every(r => r.length && isPatternValid(r))) || !!rule.tags?.length;
}